import _ from 'lodash';
import React from 'react';

import { CycleContext, RegieContext } from '@/contexts';
import type { CycleContextType } from '@/contexts/CycleContext';
import useListeSearchSort from '@/hooks/useListeSearchSort';
import {
  getCycles,
  getCyclesAccueil,
  getCyclesImpayesV2,
  getCyclesPrelevement,
  searchCycles,
} from '@/routes';
import type { CycleType } from '@/types';

/** Hors du composant : useListeSearchSort garde fixedParams dans une ref,
 * la valeur doit être stable */
const FIXED_PARAMS_IMPAYES = { perPage: 500 };

type CycleContextProviderProps = {
  children: React.ReactNode;
  // Pour la page d'accueil
  accueil?: boolean;
  // Pour la page des impayes
  impayes?: boolean;
  // Pour la page des prelèvements
  avecPrelevement?: boolean;
  // Pour charger tous les cycles d'un coup
  allAtOnce?: boolean;
};

const CycleContextProvider = ({
  children,
  accueil = false,
  impayes = false,
  avecPrelevement = false,
  allAtOnce = false,
}: CycleContextProviderProps) => {
  const { selectedRegie } = React.useContext(RegieContext);

  const selectedRegieRef = React.useRef<number>(selectedRegie);

  const {
    items,
    setItems,
    setPage,
    loading,
    sorts,
    setSorts,
    filters,
    setFilters,
    total,
    setTotal,
    search,
    setSearch,
    reset,
  } = useListeSearchSort({
    active: !_.isNil(selectedRegie),
    getItems: accueil
      ? getCyclesAccueil
      : impayes
        ? getCyclesImpayesV2
        : avecPrelevement
          ? getCyclesPrelevement
          : getCycles,
    searchItems: impayes ? getCyclesImpayesV2 : searchCycles,
    all: allAtOnce,
    // Les cycles impayés s'exportent (CSV / impression) : toute la liste doit être chargée,
    // en une seule requête dans la plupart des cas
    fixedParams: impayes ? FIXED_PARAMS_IMPAYES : null,
  });

  /** Reset la liste des cycles quand on change de régie */
  React.useEffect(() => {
    if (selectedRegieRef.current !== selectedRegie) {
      reset();
      selectedRegieRef.current = selectedRegie;
    }
  }, [reset, selectedRegie]);

  const context = React.useMemo<CycleContextType>(
    () => ({
      total,
      loading,
      cycles: items as CycleType[],
      filters,
      search,
      sorts,
      setCycles: setItems,
      setFilters,
      setSearch,
      setSorts,
      setPage,
      setTotal,
      getCycles: reset,
    }),
    [
      total,
      loading,
      items,
      filters,
      search,
      sorts,
      setItems,
      setFilters,
      setSearch,
      setSorts,
      setPage,
      setTotal,
      reset,
    ],
  );

  return <CycleContext.Provider value={context}>{children}</CycleContext.Provider>;
};

export default CycleContextProvider;
